import type {Organization} from '~/types/api';
import {isFeatureEnabled, isOrgFeatureEnabled} from './posthogFeatureFlags';
import {useNuxtApp} from 'nuxt/app';

interface LdClientLike {
  variation: (_flag: string, _defaultValue: unknown) => Promise<unknown>;
}

// Helper function to get the LaunchDarkly client.
// When undefined (e.g. during SSR or before client plugins run), returns a mock
// that yields the default value so pages don't crash.
const getLdClient = (): LdClientLike => {
  const nuxtApp = useNuxtApp();
  const ld = nuxtApp.$ldClient as LdClientLike | undefined;
  if (!ld || typeof ld.variation !== 'function') {
    return {
      variation: async (_f: string, defaultValue: unknown) => defaultValue,
    };
  }
  return ld;
};

/** Typed wrapper for feature flag lookups */
const variation = async <T>(flag: string, defaultValue: T): Promise<T> => {
  const ld = getLdClient();
  return (await ld.variation(flag, defaultValue)) as T;
};

// Feature flag functions
export const hasBusinessUnit = async (): Promise<boolean> => variation('business-unit', false);

export const getBusinessUnitLimit = async (): Promise<number> =>
  variation('business-unit-limit', 0);

export const showLimitForGrowth = async (): Promise<boolean> =>
  variation('show-limit-for-growth', false);

export const getAdContactSyncLimit = async (): Promise<number> =>
  variation('ad-contact-sync-limit', 0);

export const getNonAdContactSyncLimit = async (): Promise<number> =>
  variation('non-ad-contact-sync-limit', 0);

export const hasAdReveal = async (): Promise<boolean> => variation('ad-reveal', false);

export const hasScheduledCsvExport = async (): Promise<boolean> =>
  variation('scheduled-csv-export', false);

export const hasNewSegmentsPage = async (): Promise<boolean> =>
  variation('new-segments-page', false);

export const hasNewSegmentsListPage = async (): Promise<boolean> =>
  variation('new-segment-list', false);

export const hasCampaignReporting = async (): Promise<boolean> =>
  variation('campaign-reporting', false);

export const hasNewVisitorFeed = async (): Promise<boolean> => variation('new-visitor-feed', false);

export const hasMetaAds = async (): Promise<boolean> => variation('meta-ads', false);

/** VIP billing treatment: orgs on the identified-visitors-vip-list PostHog flag */
export const isVipListMember = (orgId?: number): Promise<boolean> =>
  isFeatureEnabled('identified-visitors-vip-list', orgId, false);

export const hasSourceTypeFilter = (): Promise<boolean> =>
  isFeatureEnabled('source-type-filter', undefined, false);

export const hasTargetContactsStats = async (): Promise<boolean> =>
  variation('has-target-contacts-stats-cards', false);

export const hasLinkedinMigration = async (): Promise<boolean> =>
  variation('linkedin-migration', false);

export const hasNewIpToCompany = async (): Promise<boolean> =>
  variation('has-new-ip-to-company', false);

export const hasIcpMemberPreview = async (): Promise<boolean> =>
  variation('has-icp-member-preview', false);

export const hasCtvCampaign = async (): Promise<boolean> => variation('ctv-campaign', false);

/** Org-scoped alpha: Salesforce Lead list views for target contact segments */
export const hasSalesforceLeadListSync = async (): Promise<boolean> =>
  variation('salesforce-lead-list-sync', false);

/** Org-scoped alpha: Salesforce Contact/Lead reports for target contact segments */
export const SALESFORCE_REPORT_LIST_SYNC_FLAG = 'salesforce-report-list-sync';

export const hasSalesforceReportListSync = async (
  orgId?: string | number | null
): Promise<boolean> => {
  if (orgId == null) return false;
  return isOrgFeatureEnabled(SALESFORCE_REPORT_LIST_SYNC_FLAG, orgId, false);
};

/** Enforce onboarding terms modal for orgs created after the configured cutoff date */
export const getNewSignupFlowDate = async (): Promise<string> =>
  variation('new-signup-flow-date', '2026-05-17');

// Generic function for dynamic feature flags
export const getFeatureFlag = async <T = unknown>(
  flagName: string,
  defaultValue: T = false as T
): Promise<T> => variation(flagName, defaultValue);

export interface FeatureFlags {
  businessUnit: boolean;
  businessUnitLimit: number;
  showLimitForGrowth: boolean;
  adContactSyncLimit: number;
  nonAdContactSyncLimit: number;
  adReveal: boolean;
  scheduledCsvExport: boolean;
  newSegmentsPage: boolean;
  campaignReporting: boolean;
  newVisitorFeed: boolean;
  metaAds: boolean;
  targetContactsStats: boolean;
  ctvCampaign: boolean;
  /** Salesforce Lead list views for integration target contact segments */
  salesforceLeadListSync: boolean;
  /** Salesforce Contact/Lead reports for integration target contact segments */
  salesforceReportListSync: boolean;
  linkedinMigration: boolean;
  newSignupFlowDate: string;
}

// Function to get all feature flags at once (useful for components that need multiple flags)
export const getAllFeatureFlags = async (): Promise<FeatureFlags> => {
  const org = useState<Organization>('org');
  const salesforceReportListSync = await hasSalesforceReportListSync(org.value?.id);
  const [
    businessUnit,
    businessUnitLimit,
    showLimitForGrowthFlag,
    adContactSyncLimit,
    nonAdContactSyncLimit,
    adReveal,
    scheduledCsvExport,
    newSegmentsPage,
    campaignReporting,
    newVisitorFeed,
    metaAds,
    targetContactsStats,
    ctvCampaign,
    salesforceLeadListSync,
    linkedinMigration,
    newSignupFlowDate,
  ] = await Promise.all([
    hasBusinessUnit(),
    getBusinessUnitLimit(),
    showLimitForGrowth(),
    getAdContactSyncLimit(),
    getNonAdContactSyncLimit(),
    hasAdReveal(),
    hasScheduledCsvExport(),
    hasNewSegmentsPage(),
    hasCampaignReporting(),
    hasNewVisitorFeed(),
    hasMetaAds(),
    hasTargetContactsStats(),
    hasCtvCampaign(),
    hasSalesforceLeadListSync(),
    hasLinkedinMigration(),
    getNewSignupFlowDate(),
  ]);

  return {
    businessUnit,
    businessUnitLimit,
    showLimitForGrowth: showLimitForGrowthFlag,
    adContactSyncLimit,
    nonAdContactSyncLimit,
    adReveal,
    scheduledCsvExport,
    newSegmentsPage,
    campaignReporting,
    newVisitorFeed,
    metaAds,
    targetContactsStats,
    ctvCampaign,
    salesforceLeadListSync,
    salesforceReportListSync,
    linkedinMigration,
    newSignupFlowDate,
  };
};
